import React, { useEffect, useState } from "react";
import api from "../../api/axios";
import Swal from "sweetalert2";

export default function ApplyLeave() {
  const [form, setForm] = useState({
    leave_type_id: "",
    duration_unit: "",
    from_date: "",
    to_date: "",
    remarks: "",
  });

  const [user, setUser] = useState(null);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [leaveHistory, setLeaveHistory] = useState([]);
  const [loadingLeaveType, setLoadingLeaveType] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(true); // 👈 added loading state for table

  const update = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  // Load user profile
  const loadUser = async () => {
    try {
      const res = await api.get("/user/profile");
      setUser(res.data);
    } catch (err) {
      console.error("Failed to load user", err);
    }
  };

  // Load Leave Types
  const loadLeaveTypes = async () => {
    try {
      const res = await api.get("/user/leave/types");
      setLeaveTypes(res.data);
    } catch (err) {
      console.error("Failed to load leave types", err);
    } finally {
      setLoadingLeaveType(false);
    }
  };

  // Load Leave History
  const loadLeaveHistory = async () => {
    try {
      setLoadingHistory(true); // 👈 start loader
      const res = await api.get("/user/leave/history");
      setLeaveHistory(res.data);
    } catch (err) {
      console.error("Failed to load history", err);
    } finally {
      setLoadingHistory(false); // 👈 stop loader
    }
  };

  useEffect(() => {
    loadUser();
    loadLeaveTypes();
    loadLeaveHistory();
  }, []);

  const toYMD = (date) => {
    if (!date) return "";
    const d = new Date(date);
    return d.toISOString().split("T")[0];
  };

  // Submit Leave
  const submitLeave = async () => {
    try {
      const payload = { ...form };
      await api.post("/user/leave/apply", payload);

      Swal.fire({
        icon: "success",
        title: "Leave Submitted!",
        text: "Your leave request has been submitted successfully.",
        confirmButtonColor: "#3085d6",
      });

      loadLeaveHistory(); // refresh after submit
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Failed to Apply Leave",
        text: err?.response?.data?.message || "Something went wrong!",
        confirmButtonColor: "#d33",
      });
    }
  };

  const format = (d) => (d ? d.split("T")[0] : "-");

  return (
    <div className="container py-4 applyleave">
      <h3 className="fw-bold mb-1">Apply Leave</h3>
      <p className="text-muted mb-4">Request a leave for your working days.</p>

      <div className="card shadow-sm rounded-4 mb-4 border-0">
        <div className="card-body p-4">
          <div className="row g-4">
            {/* Employee */}
            <div className="col-md-3">
              <label className="form-label fw-semibold">Employee</label>
              <input
                type="text"
                className="form-control p-3 rounded-3"
                value={
                  user ? `${user.name} (${user.employee_code})` : "Loading..."
                }
                disabled
              />
            </div>

            {/* Leave Type */}
            <div className="col-md-3">
              <label className="form-label fw-semibold">Leave Type</label>
              <select
                className="form-select p-3 rounded-3"
                value={form.leave_type_id}
                onChange={(e) => update("leave_type_id", e.target.value)}
              >
                <option value="">Select Leave Type</option>
                {loadingLeaveType ? (
                  <option>Loading...</option>
                ) : (
                  leaveTypes.map((lt) => (
                    <option key={lt.id} value={lt.id}>
                      {lt.name}
                    </option>
                  ))
                )}
              </select>
            </div>

            {/* Duration */}
            <div className="col-md-2">
              <label className="form-label fw-semibold">Duration</label>
              <select
                className="form-select p-3 rounded-3"
                value={form.duration_unit}
                onChange={(e) => update("duration_unit", e.target.value)}
              >
                <option value="">Select</option>
                <option value="full">Full Day</option>
                <option value="half">Half Day</option>
              </select>
            </div>

            <div className="col-md-2">
              <label className="form-label fw-semibold">From Date</label>
              <input
                type="date"
                className="form-control p-3 rounded-3"
                value={form.from_date}
                min={toYMD(new Date())}
                onChange={(e) => {
                  update("from_date", e.target.value);
                  update("to_date", "");
                }}
              />
            </div>

            <div className="col-md-2">
              <label className="form-label fw-semibold">To Date</label>
              <input
                type="date"
                className="form-control p-3 rounded-3"
                value={form.to_date}
                disabled={!form.from_date}
                min={form.from_date || new Date().toISOString().split("T")[0]}
                onChange={(e) => update("to_date", e.target.value)}
              />
            </div>
          </div>

          {/* Remarks */}
          <div className="row mt-4">
            <div className="col-md-4">
              <label className="form-label fw-semibold">Remarks</label>
              <input
                type="text"
                className="form-control p-3 rounded-3"
                placeholder="Remarks"
                value={form.remarks}
                onChange={(e) => update("remarks", e.target.value)}
              />
            </div>
          </div>

          {/* Submit */}
          <button
            className="applyleave mt-4 px-4 py-2 fw-semibold rounded-3"
            onClick={submitLeave}
          >
            Submit
          </button>
        </div>
      </div>

      {/* Leave History Table */}
      <h4 className="fw-bold mb-3">Leave History</h4>

      <div className="card shadow-sm rounded-4 border-0">
        <div className="table-responsive p-3">
          <table className="table align-middle">
            <thead className="table-light">
              <tr>
                <th>LEAVE TYPE</th>
                <th>FROM</th>
                <th>TO</th>
                <th>DURATION</th>
                <th>REMARKS</th>
                <th>STATUS</th>
              </tr>
            </thead>

            <tbody>
              {loadingHistory ? (
                // 👇 Loader row inside table
                <tr>
                  <td colSpan="6" className="text-center py-5">
                    <div className="d-flex flex-column align-items-center">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                        style={{ width: "2.5rem", height: "2.5rem" }}
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="text-muted mt-3 mb-0 fw-semibold">
                        Loading leave history...
                      </p>
                    </div>
                  </td>
                </tr>
              ) : leaveHistory.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-4 text-muted">
                    No leave history found.
                  </td>
                </tr>
              ) : (
                leaveHistory.map((lv, i) => (
                  <tr key={i}>
                    <td>{lv.leave_type}</td>
                    <td>{format(lv.from_date)}</td>
                    <td>{format(lv.to_date)}</td>
                    <td>{lv.duration_unit}</td>
                    <td>{lv.remarks}</td>
                    <td>
                      <span
                        className={`badge ${
                          lv.status === "approved"
                            ? "bg-success"
                            : lv.status === "pending"
                            ? "bg-warning text-dark"
                            : "bg-danger"
                        }`}
                      >
                        {lv.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
